<template>
  <BaseVeeForm :action="submit">
    <template #default="formProps">
      <div class="space-y-16">
        <div class="space-y-6 sm:space-y-5">
          <div>
            <h3 class="text-lg leading-6 font-medium text-gray-900">General Settings</h3>
            <p class="mt-1 max-w-2xl text-sm text-gray-500">Setup sender and message.</p>
          </div>

          <div class="space-y-8">
            <BaseFormSelectFieldWithValidator
              v-model="formData.campaign_type"
              v-bind="campaignTypeFieldProps"
            />

            <BaseFormSelectFieldWithValidator
              v-model="formData.from"
              v-bind="fromFieldProps"
              :disabled="disabled || formProps.isPending"
            />

            <BaseFormTextareaFieldWithValidator
              v-model="formData.content"
              v-bind="smsTextProps"
              :disabled="disabled || formProps.isPending"
              class="max-w-lg"
            />
          </div>
        </div>

        <div class="space-y-6 sm:space-y-5">
          <div>
            <h3 class="text-lg leading-6 font-medium text-gray-900">Recipients Information</h3>
            <p class="mt-1 max-w-2xl text-sm text-gray-500">
              Add conditions to filter final recipients.
            </p>
          </div>

          <RecipientList
            :key="recipientListKey"
            :account-id="accountId"
            :query-filter="formData.filter"
            @update-filter="formData.filter = $event"
            :disabled="disabled || formProps.isPending"
          />
        </div>

        <div class="space-y-6 sm:space-y-5">
          <div>
            <h3 class="text-lg leading-6 font-medium text-gray-900">Schedule Information</h3>
            <p class="mt-1 max-w-2xl text-sm text-gray-500">
              Select when to start sending the sms.
            </p>
          </div>

          <div class="space-y-2">
            <div>
              <BaseFormRadio
                v-model="formData.start_now"
                name="starts_at"
                label="Send immediately"
                :checked-value="true"
                :disabled="disabled || formProps.isPending"
              />
            </div>
            <div>
              <BaseFormRadio
                v-model="formData.start_now"
                name="starts_at"
                label="Start sending SMS at"
                :checked-value="false"
                :disabled="disabled || formProps.isPending"
              />
              <div class="pl-7">
                <BaseFormTextFieldWithValidator
                  v-if="formData.start_now === false"
                  v-model="formData.starts_at"
                  :validator="{
                    name: 'starts at',
                    vid: 'starts_at',
                    rules: {
                      required: true,
                    },
                  }"
                  :field="{
                    id: 'sms_campaign_starts_at',
                    inputType: 'datetime-local',
                  }"
                  :disabled="disabled || formProps.isPending"
                  class="max-w-lg"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col space-y-3">
          <BaseButton primary :disabled="disabled || formProps.invalid || formProps.isPending">
            {{ exists ? 'Update' : 'Send' }}
          </BaseButton>
          <ValidationProvider
            name="confirmed"
            slim
            vid="confirmed"
            :rules="{ required: { allowFalse: false } }"
          >
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormCheckbox
                  v-model="formData.confirmed"
                  :checked-value="true"
                  class="inline-flex mx-auto"
                >
                  <span class="ml-3">I confirm that the campaign is ready to get started.</span>
                </BaseFormCheckbox>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>
        </div>
      </div>
    </template>
  </BaseVeeForm>
</template>

<script>
import { omit } from 'lodash-es';
import moment from 'moment';
import RecipientList from './RecipientList.vue';

export default {
  components: {
    RecipientList,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    fromOptions: {
      type: Array,
      default: () => [],
    },
    defaultData: {
      type: Object,
      default: () => ({}),
    },
    action: {
      type: Function,
      required: true,
    },
    disabled: Boolean,
  },
  data() {
    return {
      formData: {
        campaign_type: this.defaultData?.campaign_type || null,
        from: this.defaultData?.third_party_service_id || null,
        content: this.defaultData?.sms_message?.content || '',
        filter: this.defaultData?.filter || {},
        starts_at: this.defaultData?.starts_at
          ? moment.utc(this.defaultData.starts_at).local().format('YYYY-MM-DDTHH:mm')
          : null,
        start_now: typeof this.defaultData?.starts_at !== 'string',
        confirmed: false,
      },
    };
  },
  computed: {
    exists() {
      return typeof this.defaultData?.id === 'string';
    },
    recipientListKey() {
      return JSON.stringify(this.formData.filter);
    },
    campaignTypeFieldProps() {
      return {
        validator: {
          name: 'campaign_type',
          vid: 'campaign_type',
          rules: { required: true },
        },
        field: {
          id: 'campaing_type',
          label: 'Campaign type',
          options: [
            {
              value: 'transactional',
              label: 'Transactional',
            },
            {
              value: 'marketing',
              label: 'Marketing',
            },
          ],
        },
      };
    },
    fromFieldProps() {
      return {
        validator: {
          name: 'from',
          vid: 'from',
          rules: {
            required: true,
          },
        },
        field: {
          id: 'from',
          label: 'SMS From',
          options: this.fromOptions,
        },
      };
    },
    smsTextProps() {
      return {
        validator: {
          name: 'content',
          vid: 'content',
          rules: {
            required: true,
          },
        },
        field: {
          id: 'content',
          label: 'SMS Content',
        },
      };
    },
  },
  methods: {
    submit() {
      if (this.disabled) {
        return Promise.reject();
      }

      return this.action({
        ...omit(this.formData, ['start_now', 'starts_at']),
        ...(this.formData.start_now
          ? { start_now: true }
          : { starts_at: moment(this.formData.starts_at).utc().format('YYYY-MM-DD HH:mm') }),
      }).then((response) => {
        this.$emit('saved');

        return Promise.resolve(response);
      });
    },
  },
};
</script>
